import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Buildings, CheckCircle, XCircle, Spinner, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { hydrateSession } = useAuth();
  const token = params.get("token") || "";
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token"); return; }
    api.get(`/invitations/lookup?token=${encodeURIComponent(token)}`)
      .then((r) => setInv(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invalid invitation"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const r = await api.post("/invitations/accept", { token, password });
      await hydrateSession(r.data.token, r.data.user);
      toast.success("Welcome aboard");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accept failed");
    } finally { setLoading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="max-w-sm text-center space-y-3" data-testid="invite-error">
          <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <XCircle size={28} weight="duotone" className="text-destructive" />
          </div>
          <h2 className="font-heading text-2xl font-bold">Invitation not valid</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/login"><Button variant="outline" className="rounded-full">Go to sign in</Button></Link>
        </div>
      </div>
    );
  }

  if (!inv) return <div className="min-h-screen flex items-center justify-center"><Spinner size={28} className="animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="accept-form" autoComplete="off">
        <Brand />
        <div className="p-4 rounded-lg bg-secondary/40 border border-border">
          <div className="flex items-start gap-3">
            <Buildings size={18} weight="duotone" className="text-primary mt-0.5" />
            <div>
              <div className="overline text-muted-foreground text-[10px]">Joining</div>
              <div className="font-heading font-bold text-base leading-tight">{inv.organization?.name}</div>
              <div className="text-xs text-muted-foreground mt-1">As <span className="font-medium capitalize">{inv.role.replace("_"," ")}</span> · Invited by {inv.invited_by_name}</div>
            </div>
          </div>
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Set your password</h2>
          <p className="text-sm text-muted-foreground mt-1">Signed in as <span className="font-mono text-xs">{inv.email}</span></p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">NEW PASSWORD</Label>
            <div className="relative mt-1.5">
              <Input type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                placeholder="At least 8 characters" data-testid="accept-password-input" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">CONFIRM PASSWORD</Label>
            <Input type={show ? "text" : "password"} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password"
              placeholder="Repeat password" className="mt-1.5" data-testid="accept-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="accept-submit-button">
          {loading ? "Joining…" : "Accept & join"}
        </Button>
      </form>
    </div>
  );
}
